"use client";

import { Shuffle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useLang, useUi } from "@/components/ui/use-ui";
import { cn } from "@/lib/cn";
import { localePath, splitLocale } from "@/lib/i18n";

// "Random video": asks /api/random for one (not the one being watched) and goes there without a reload;
// /random does the same for links opened in a new tab.

/** Goes to a random video; `busy` while it's being picked. */
export function useRandomVideo() {
  const router = useRouter();
  const lang = useLang();
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const current = splitLocale(window.location.pathname).path.match(/^\/-([0-9a-z]+)$/)?.[1];
      const res = await fetch(`/api/random${current ? `?not=${current}` : ""}`);
      const { id } = (await res.json()) as { id: string | null };
      router.push(localePath(lang, id ? `/-${id}` : "/videos"));
    } catch {
      window.location.href = localePath(lang, "/random");
    } finally {
      setBusy(false);
    }
  };
  return { go, busy };
}

/** The top bar's button. */
export function RandomVideoButton() {
  const t = useUi();
  const { go, busy } = useRandomVideo();
  return (
    <button
      type="button"
      onClick={go}
      aria-label={t.nav.random}
      title={t.nav.random}
      className="grid size-11 place-items-center rounded-full text-fg transition-colors hover:bg-white/10"
    >
      <Shuffle size={21} strokeWidth={1.6} className={cn(busy && "animate-spin [animation-duration:0.8s]")} />
    </button>
  );
}
